/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    Gauge,
    type LucideIcon,
} from 'lucide-react';
import { Fragment } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import {
    ElectricityIcon,
    GasIcon,
    HeatIcon,
    PhotovoltaicsIcon,
    WaterIcon,
} from '@/components/icon/SupplyIcon';

/* Import libraries */
import { NamingConventions } from '@/lib/formatting/namingConventions';

export interface SystemAnomalyIconCount {
    anomaly: string;
    sensorCount: number;
}

interface AnomalyIconConfig {
    accent: 'electricity' | 'gas' | 'heat' | 'photovoltaics' | 'water';
    icon: LucideIcon;
}

interface SystemAnomalyIconsProps {
    anomalySensorCounts: readonly SystemAnomalyIconCount[];
}

const ANOMALY_ICON_ORDER = [
    'pv',
    'bl',
    'grid_feed',
    'heating_energy',
    'gas_bl',
    'water_bl',
] as const;

export function getSystemAnomalyLabel(
    anomaly: string,
    t: (key: string) => string,
): string {
    switch (anomaly) {
        case 'bl':
            return t('Base Load');
        case 'bl_night':
            return t('Base Load Night');
        case 'heating_energy':
            return t('Heating Energy');
        case 'water_bl':
            return t('Water');
        case 'gas_bl':
            return t('Gas');
        case 'grid_feed':
            return t('Grid Feed');
        case 'pv':
            return t('Photovoltaic');
        default:
            return new NamingConventions(anomaly).getTitle();
    }
}

function getAnomalyIconConfig(anomaly: string): AnomalyIconConfig {
    switch (anomaly) {
        case 'pv':
            return { accent: 'photovoltaics', icon: PhotovoltaicsIcon };
        case 'heating_energy':
            return { accent: 'heat', icon: HeatIcon };
        case 'gas_bl':
            return { accent: 'gas', icon: GasIcon };
        case 'water_bl':
            return { accent: 'water', icon: WaterIcon };
        case 'bl':
        case 'bl_night':
        case 'grid_feed':
            return { accent: 'electricity', icon: ElectricityIcon };
        default:
            return { accent: 'electricity', icon: Gauge };
    }
}

function getAnomalyIconCounts(
    anomalySensorCounts: readonly SystemAnomalyIconCount[],
): SystemAnomalyIconCount[] {
    const iconCounts = anomalySensorCounts.reduce<SystemAnomalyIconCount[]>((counts, { anomaly, sensorCount }) => {
        const normalizedAnomaly = anomaly === 'bl_night' ? 'bl' : anomaly;
        const existingCount = counts.find(({ anomaly: existingAnomaly }) => {
            return existingAnomaly === normalizedAnomaly;
        });

        if (existingCount) {
            existingCount.sensorCount += sensorCount;
        } else {
            counts.push({ anomaly: normalizedAnomaly, sensorCount });
        }

        return counts;
    }, []);

    return iconCounts.sort((left, right) => {
        const leftIndex = ANOMALY_ICON_ORDER.indexOf(left.anomaly as typeof ANOMALY_ICON_ORDER[number]);
        const rightIndex = ANOMALY_ICON_ORDER.indexOf(right.anomaly as typeof ANOMALY_ICON_ORDER[number]);

        return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
            - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
    });
}

function SystemAnomalyIcons({ anomalySensorCounts }: SystemAnomalyIconsProps) {
    const { t } = useI18n();
    const iconCounts = getAnomalyIconCounts(anomalySensorCounts);

    return (
        <div className="flex items-center" aria-label={t('Anomalies')}>
            {iconCounts.map(({ anomaly, sensorCount }, index) => {
                const { accent, icon: Icon } = getAnomalyIconConfig(anomaly);
                const sensorLabel = sensorCount === 1 ? 'sensor' : 'sensors';

                return (
                    <Fragment key={anomaly}>
                        {index > 0 ? (
                            <span className="mx-px h-5 w-px bg-base-content/30" aria-hidden="true" />
                        ) : null}
                        <span
                            className={`tooltip tooltip-end inline-flex items-center gap-0.5 px-1 tooltip-top ${
                                index === 0 ? 'pl-0' : ''
                            } ${index === iconCounts.length - 1 ? 'pr-0' : ''}`}
                            data-tip={t(`{sensorCount} {anomaly} ${sensorLabel}`, {
                                anomaly: getSystemAnomalyLabel(anomaly, t),
                                sensorCount,
                            })}
                        >
                            <Icon
                                className={`overview-system-card__anomaly-icon overview-system-card__anomaly-icon--${accent}`}
                                aria-hidden="true"
                            />
                            <span className="text-xs font-semibold tabular-nums text-base-content/75">
                                <sup>{sensorCount}</sup>
                            </span>
                        </span>
                    </Fragment>
                );
            })}
        </div>
    );
}

export default SystemAnomalyIcons;
